import React, { Fragment, memo, useEffect, useState } from 'react'

import { formatPriceText, ProductV2, ShortGameId, GameId, StoreOfferProduct } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useRouter } from 'next/router'

import CrGemIcon from '@/assets/images/icons/cr_icon_gem.png'
import { useGames } from '@/features/common/useGames'
import { useLocale } from '@/features/locale/useLocale'
import { ItemRow, ItemTitle, ItemDescription, ItemImages } from '@/features/products/ItemRow'
import ProductDescription from '@/features/products/ProductDescription'
import { useOfferCountdown } from '@/features/products/useOfferCountdown'
import { isStoreSpecial, isNormalOffer } from '@/features/storefronts//productFilters'
import { OfferValueBadge } from '@/features/storefronts/_storeBase/offerValueBadge/OfferValueBadge'
import { TimeRemaining } from '@/features/storefronts/_storeBase/timeRemaining/TimeRemaining'
import { useMessageContext } from '@/localization/MessageContext'
import { useTranslatable } from '@/localization/useTranslatable'
import { gtmProductView } from '@/utils/gtm'
import { getProductCurrencyType, isSeasonPass } from '@/utils/products'
import { snowplowTrackProductView } from '@/utils/snowplow'
import { useAppSelector } from '@/utils/useAppSelector'

import { useModalContext } from '../../../../contexts/ModalContext'
import { isGemPackOffer } from '../../clashroyale'
import { BuyButton } from '../BuyButton/BuyButton'
import { BrawlCardProduct } from '../card/BrawlCardProduct'
import { CardProduct } from '../card/CardProduct'
import { StoreTypography } from '../typography/StoreTypography'
import { PassesInfo } from './PassesInfo'
import { ProductInfo } from './ProductInfo'
import { SpecialOfferHero, NormalOfferHero, SingleProducHero, GemPackOfferHero } from './ProductPageHeader'

import styles from './gameStoreProduct.module.scss'

interface ProductProps {
  product: ProductV2
  isProductPage: boolean
  closeModal?: () => void
}

//TODO: translations

export const GameStoreProduct: React.FC<ProductProps> = memo(({ product, isProductPage, closeModal }) => {
  const { openModal } = useModalContext()
  const router = useRouter()
  const market = useAppSelector((state) => state.config.market)
  const locale = useLocale()
  // Currency is not available if country is not supported
  const currencyCountry = market?.currencyCountry
  const currency = market?.currencyCode

  const games = useGames()
  const translatable = useTranslatable()
  const viewerCountry = useAppSelector((state) => state.config.viewerCountry)
  const game = games.find((g) => g.slug === product.game)

  const { isTimedOffer, countdownExpired, timeLeftText } = useOfferCountdown({ product: product, threshold: 30 })
  const showOriginalPrice = product.offerData?.isDiscount && currencyCountry && currency
  const originalPrice = product.offerData && 'originalPrice' in product?.offerData && product.offerData.originalPrice

  const isOffer = isStoreSpecial(product) || isNormalOffer(product)
  const isGoldPass = isSeasonPass(product) && product.id.includes('magic.goldpass')

  useEffect(() => {
    gtmProductView(product)
  }, [locale, product, viewerCountry])

  useEffect(() => {
    if (currencyCountry && currency) {
      snowplowTrackProductView(product, locale, currencyCountry, currency)
    }
  }, [locale, product, currencyCountry, currency])

  useEffect(() => {
    if (isTimedOffer && countdownExpired) {
      openModal({ type: 'OFFER_EXPIRED', props: { expired: true } })
    }
  }, [isTimedOffer, countdownExpired, openModal])

  if (!game) {
    router.push('/404')
    return null
  }

  return (
    <div className={cx(styles.ProductContainer, { [styles.ProductPage]: isProductPage })}>
      <div
        className={cx(styles.Product, {
          [styles.IsPopup]: isProductPage === false,
        })}
      >
        <div className={cx(styles.ProductHeaderContainer)}>
          <StoreTypography tag="h1" style="outlined" textSize="lg" className={styles.Title}>
            {translatable(product.title)}
          </StoreTypography>
          <div className={styles.productHero}>
            {isOffer && <OfferValueBadge offerProduct={product as StoreOfferProduct} game={game.slug} />}
            {isOffer ? (
              isStoreSpecial(product) ? (
                <SpecialOfferHero alt={translatable(product.title)} product={product} game={game.slug} />
              ) : isGemPackOffer(product) ? (
                <GemPackOfferHero alt={translatable(product.title)} product={product} game={game.slug} />
              ) : (
                <NormalOfferHero alt={translatable(product.title)} product={product} game={game.slug} />
              )
            ) : (
              <SingleProducHero product={product} alt={translatable(product.title)} game={game.slug} />
            )}
          </div>
        </div>

        <div
          className={cx(styles.Content, {
            [styles.IsPopup]: isProductPage === false,
          })}
        >
          <div className={styles.offerDetailsWrapper}>
            {isTimedOffer && timeLeftText.length > 0 && (
              <div className={styles.TimerContainer}>
                <TimeRemaining timeLeftText={timeLeftText} />
              </div>
            )}

            {isGemPackOffer(product) && (
              <div className={styles.TimerContainer}>
                <Image src={CrGemIcon} alt="gem" height={32} width={32} sizes="50px" />
                <StoreTypography tag="span" textSize="md" style="outlined">
                  {product.contentCounts.GEM}
                </StoreTypography>
              </div>
            )}

            <div className={styles.Action}>
              <BuyButton
                disabled={isTimedOffer && countdownExpired}
                additionalClasses={styles.BuyButton}
                type="Primary"
                product={product}
                content={currencyCountry ? formatPriceText(product.prices[currencyCountry], locale, currency) : ''}
                isLocked={!market}
                data-cy={`product-${product.id}`}
                onAddToCart={closeModal}
              />
              {showOriginalPrice && originalPrice && (
                <p className={styles.OriginalPrice}>
                  {formatPriceText(originalPrice[currencyCountry], locale, currency)}
                </p>
              )}
            </div>
          </div>

          <div className={styles.descriptionWrapper}>
            {!isOffer && (
              <ProductDescription product={product} tag="h2" className={styles.Description} align="left" card />
            )}
            {isGoldPass && <PassesInfo product={product} />}

            {isSeasonPass(product) ? (
              <SeasonPassProductContents product={product} />
            ) : (
              <ProductExtraDescription product={product} shortSlug={game.shortSlug} />
            )}

            {isStoreSpecial(product) && (
              <div className={styles.offerDescription}>
                Store Specials give you the the best value anywhere! Check back often to see the current offers.
              </div>
            )}
          </div>

          {product.type === 'offer' && !isSeasonPass(product) && !isGemPackOffer(product) && (
            <OfferContent product={product} game={game.slug} />
          )}
        </div>

        <ProductInfo game={game.slug} />
      </div>
    </div>
  )
})

GameStoreProduct.displayName = 'GameStoreProduct'

const OfferContent = ({ product, game }: { product: ProductV2; game: GameId }) => {
  const storeSpecial = isStoreSpecial(product)
  const brawl = game === 'brawlstars'
  return (
    <div className={styles.offerContentContainer}>
      <div className={styles.subtitle}>Included in the bundle</div>

      <div className={styles.productsContainer}>
        {product.contents.map((content, i) => (
          <Fragment key={i}>
            {brawl ? (
              <BrawlCardProduct size="size-product-page" game={game} content={content} productPage={true} />
            ) : (
              <CardProduct
                size="size-product-page"
                game={game}
                content={content}
                productPage={true}
                storeSpecial={storeSpecial}
              />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

const ProductExtraDescription = ({ product, shortSlug }: { product: ProductV2; shortSlug: ShortGameId }) => {
  const t = useTranslations()
  const translate = useTranslatable()
  const contentType = getProductCurrencyType(product)
  const offerDescription = product.type === 'offer' ? translate(product.description, '') : ''

  const escapedNewLine = offerDescription.replace(/\\n/g, '\n')
  const escapedOfferDescr = escapedNewLine
    .split('\n')
    .filter(Boolean)
    .map((str, index) => (
      <StoreTypography
        tag="p"
        style="plain"
        textSize="body"
        overrideFont="supercellText"
        lineHeight="normal"
        key={index}
      >
        {str}
      </StoreTypography>
    ))
  if (offerDescription !== '') {
    return <div className={styles.extraDescription}>{escapedOfferDescr}</div>
  }

  if (!contentType) {
    return null
  }
  switch (shortSlug) {
    case 'cr':
      break
    case 'hd':
      if (contentType === 'COIN')
        return (
          <StoreTypography
            tag="p"
            style="plain"
            textSize="body"
            overrideFont="supercellText"
            lineHeight="normal"
            className={styles.extraDescription}
          >
            {t('hd_coin_description_2')}
          </StoreTypography>
        )
      if (contentType === 'GEM')
        return (
          <StoreTypography
            tag="p"
            style="plain"
            textSize="body"
            overrideFont="supercellText"
            lineHeight="normal"
            className={styles.extraDescription}
          >
            {t('hd_gem_description_2')}
          </StoreTypography>
        )
      break
    case 'bs':
      if (contentType === 'GEM')
        return (
          <StoreTypography
            tag="p"
            style="plain"
            textSize="body"
            overrideFont="supercellText"
            lineHeight="normal"
            className={styles.extraDescription}
          >
            {t('bs_gem_description_2')}
          </StoreTypography>
        )
      break
    case 'coc':
      if (contentType === 'GEM')
        return (
          <StoreTypography
            tag="p"
            style="plain"
            textSize="body"
            overrideFont="supercellText"
            lineHeight="normal"
            className={styles.extraDescription}
          >
            {t('coc_gem_description_2')}
          </StoreTypography>
        )
      break
  }
  return null
}

const SeasonPassProductContents: React.FC<{ product: ProductV2 }> = ({ product }) => {
  const [productLoaded, setProductLoaded] = useState<undefined | ProductMessageKey>(undefined)
  const t = useTranslations(productLoaded ?? undefined)
  const locale = useLocale()
  const { loadProductMessage } = useMessageContext()
  useEffect(() => {
    const fn = async () => {
      const loadRes = await loadProductMessage(product, locale)
      if (loadRes) {
        setProductLoaded(loadRes)
      }
    }
    fn()
  }, [product, locale, loadProductMessage])
  if (!productLoaded) {
    return null
  }
  return (
    <div className={styles.seasonPassProductContents}>
      <StoreTypography
        tag="h3"
        style="plain"
        textSize="body"
        overrideFont="supercellTextMedium"
        lineHeight="normal"
        className={styles.title}
      >
        Gold Pass
      </StoreTypography>
      <StoreTypography tag="p" style="plain" textSize="body" overrideFont="supercellText" lineHeight="normal">
        {t.rich('description', {
          TextBlock: (children) => {
            if (Array.isArray(children)) {
              return <span className={styles.TextBlock}>{children[0]}</span>
            }
          },
        })}
      </StoreTypography>
      <div className={styles.ProductList}>
        {t.rich('contents', {
          Title: (children) => <ItemTitle>{children}</ItemTitle>,
          Description: (children) => <ItemDescription>{children}</ItemDescription>,
          Image: (children) => {
            if (Array.isArray(children)) {
              return <ItemImages>{children[0]}</ItemImages>
            }
          },
          Row: (children) => <ItemRow>{children}</ItemRow>,
          TextBlock: (children) => {
            if (Array.isArray(children)) {
              return <span className={styles.TextBlock}>{children[0]}</span>
            }
          },
        })}
      </div>
    </div>
  )
}
